/* Danh mục cho form lịch hẹn — cùng nguồn với màn Tạo lịch hẹn (chỉ đọc); API lỗi thì dùng dữ liệu mẫu để vẫn thử được luồng */
import { useEffect, useState } from 'react';
import { getBranches } from '../../../api/Branch';
import { mixGetV1, mixPostV1, mixPutV1 } from '../../../services/mixin';
import { SVC } from '../components/Data';
import { BRANCHES } from '../dashboard/data';

export interface Opt { value: number; label: string }
export interface SvcOpt { id: number; name: string; price: number }
export interface ApptRefs { branches: Opt[]; page: Opt[]; tele: Opt[]; branchStaff: Opt[]; offline: boolean }

/* eslint-disable @typescript-eslint/no-explicit-any */
const itemsOf = (res: any): any[] => (Array.isArray(res?.data?.items) ? res.data.items : Array.isArray(res?.data) ? res.data : []);
const toOpt = (x: any): Opt => ({ value: Number(x.id), label: x.name || x.fullname || `#${x.id}` });
/* eslint-enable @typescript-eslint/no-explicit-any */
const safe = <T,>(p: Promise<T>) => p.catch(() => null);

let refsPromise: Promise<ApptRefs> | null = null;

async function loadRefs(): Promise<ApptRefs> {
  // Phòng ban: 2 = trực page, 3 = telesale, 4 = nhân viên cơ sở (như màn Tạo lịch hẹn)
  const staffs = (dept: number) => safe(mixGetV1('/api/v1/be/staffs', { type_user: 1, type_department_id: dept, limit: 200 }));
  const [b, p, t, s] = await Promise.all([safe(getBranches()), staffs(2), staffs(3), staffs(4)]);
  const branches = itemsOf(b).map(toOpt);
  return {
    // Chi nhánh mẫu có id âm → payload không gửi branch_id
    branches: branches.length ? branches : BRANCHES.map((x, i) => ({ value: -(i + 1), label: x.name })),
    page: itemsOf(p).map(toOpt),
    tele: itemsOf(t).map(toOpt),
    branchStaff: itemsOf(s).map(toOpt),
    offline: !branches.length,
  };
}

/** Chi nhánh + nhân viên trực page / telesale / cơ sở, tải một lần cho cả phiên */
export function useApptRefs() {
  const [refs, setRefs] = useState<ApptRefs | null>(null);
  useEffect(() => {
    let alive = true;
    if (!refsPromise) refsPromise = loadRefs();
    refsPromise.then((r) => {
      if (alive) setRefs(r);
      if (r.offline) refsPromise = null; // lần sau thử tải lại
    });
    return () => { alive = false; };
  }, []);
  return refs;
}

const SAMPLE_SVC: SvcOpt[] = Object.values(SVC).flat().map(([name, price], i) => ({ id: -(i + 1), name, price: price ?? 0 }));

/** Tìm dịch vụ như ô "Thêm dịch vụ" của màn Tạo lịch hẹn */
export async function searchServices(search?: string): Promise<SvcOpt[]> {
  const res = await safe(mixGetV1('/api/v1/be/services', { take: 15, status: 0, search: search || undefined }));
  if (res?.status) return itemsOf(res).map((x) => ({ id: Number(x.id), name: x.name, price: Number(x.price) || 0 }));
  const q = (search || '').trim().toLowerCase();
  return SAMPLE_SVC.filter((x) => !q || x.name.toLowerCase().includes(q)).slice(0, 15);
}

/* ---------- Ghi lịch hẹn lên hệ thống (cùng API màn Tạo lịch hẹn / chi tiết lịch hẹn) ---------- */

export type SaveResult = { id: number; code?: string } | { error: string };

/* eslint-disable @typescript-eslint/no-explicit-any */
const errOf = (res: any, fallback: string) => res?.msg || res?.message || fallback;

/** POST /api/v1/be/appointments — lịch mới hiện ngay ở danh sách Lịch hẹn */
export async function postAppointment(payload: Record<string, unknown>): Promise<SaveResult> {
  try {
    const res: any = await mixPostV1('/api/v1/be/appointments', payload);
    const id = Number(res?.data?.id ?? res?.data?.appointment_id ?? res?.data?.appointment?.id ?? 0);
    return res?.status && id > 0 ? { id, code: res.data?.code } : { error: errOf(res, 'Không tạo được lịch hẹn trên hệ thống') };
  } catch {
    return { error: 'Không kết nối được máy chủ' };
  }
}

/** PUT /api/v1/be/appointments/:id — dời giờ, xác nhận, huỷ */
export async function putAppointment(id: number, payload: Record<string, unknown>): Promise<SaveResult> {
  try {
    const res: any = await mixPutV1(`/api/v1/be/appointments/${id}`, payload);
    return res?.status ? { id, code: res.data?.code } : { error: errOf(res, 'Không cập nhật được lịch hẹn trên hệ thống') };
  } catch {
    return { error: 'Không kết nối được máy chủ' };
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */
